import { ClientContext } from "ClientProvider";
import { getTransactionByAnalysis } from "api/graphQL_dashboard";
import PieChart from "components/charts/PieChart";
import DivLoader from "components/divloader/DivLoader";
import { useEffect, useState, useContext } from "react";
import { pieChartOptions as pieChartOptionsPayload } from "variables/charts";

function calculatePercentage(obj: Object) {
  const totalSum = Object.values(obj).reduce((sum: number, value: any) => sum + value, 0);
  if (totalSum === 0) return [];
  return Object.entries(obj).map(([key, value]: any) => ({
    key,
    value: Math.round((value / totalSum) * 100),
    original: value,
  }));
}

const COLORS = pieChartOptionsPayload?.colors ?? ["#16a34a", "#dc2626", "#2563eb", "#d97706"];

const TransactionAnalysisPieChartCard = ({
  type, datesTop, timeFrameTop, selectOrg, selectClient, selectCard,
}: {
  type?: string; datesTop?: any; timeFrameTop?: any;
  selectOrg?: any; selectClient?: any; selectCard?: any;
}) => {
  const [pieChartData, setPieChartData] = useState<any>();
  const [pieChartOptions, setPieChartOptions] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { dashboardData } = useContext(ClientContext);
  const [dates, setDates] = useState<[Date, Date]>(datesTop);
  const [timeFrame, setTimeFrame] = useState<string>(timeFrameTop);

  const fetchData = () => {
    setIsLoading(true);
    let data: any = {
      time_frame: timeFrame,
      org_ids: selectOrg?.length > 0 ? selectOrg?.map((m: any) => m?.id)?.toString() : "",
      client_ids: selectClient?.length > 0 ? selectClient?.map((m: any) => m?.id)?.toString() : "",
      card_type: selectCard?.length > 0 ? selectCard?.map((m: any) => m?.value)?.toString() : "",
    };
    if (dates) { data = { ...data, start_date: dates[0].toISOString().substring(0, 10), end_date: dates[1].toISOString().substring(0, 10) }; }
    if (timeFrame !== "custom") { delete data.start_date; delete data.end_date; }
    getTransactionByAnalysis(type, dashboardData, data)
      .then((dataR) => { setPieChartData(calculatePercentage(dataR)); setPieChartOptions({ ...pieChartOptionsPayload, labels: Object.keys(dataR) }); })
      .catch(() => {}).finally(() => setIsLoading(false));
  };

  useEffect(() => { const t = setTimeout(fetchData, 1000); return () => clearTimeout(t); }, [dashboardData, dates, timeFrame, selectOrg, selectClient, selectCard]);
  useEffect(() => { setDates(datesTop); setTimeFrame(timeFrameTop); }, [datesTop, timeFrameTop]);

  const hasData = pieChartData?.filter((f: any) => f?.value)?.length > 0 && pieChartOptions;
  const total   = pieChartData?.reduce((acc: number, d: any) => acc + d.original, 0) ?? 0;

  return (
    <div className="ta-card">
      {/* ── Header ── */}
      <div className="ta-header">
        <div className="ta-header-left">
          <div className="ta-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/>
            </svg>
          </div>
          <div>
            <h3 className="ta-title">Transaction Analysis</h3>
            <p className="ta-subtitle">Approved vs declined breakdown</p>
          </div>
        </div>
        {!isLoading && total > 0 && (
          <div className="ta-total">{total.toLocaleString()} total</div>
        )}
      </div>

      {/* ── Body ── */}
      <div className="ta-body">
        {isLoading ? (
          <div className="ta-center">
            <div className="ta-spinner" />
            <p className="ta-hint">Loading analysis…</p>
          </div>
        ) : hasData ? (
          <div className="ta-layout">
            {/* Chart */}
            <div className="ta-chart-col">
              <PieChart chartOptions={pieChartOptions} chartData={pieChartData?.map((d: any) => d?.original)} chartType="pie" />
            </div>

            {/* Breakdown cards */}
            <div className="ta-breakdown">
              {pieChartData?.filter((f: any) => f?.value).map((d: any, i: number) => {
                const color = COLORS[i % COLORS.length];
                const isApproved = d.key?.toUpperCase().includes("APPROVED");
                const isDeclined = d.key?.toUpperCase().includes("DECLINED") || d.key?.toUpperCase().includes("DECLINE");
                const bg     = isApproved ? "#f0fdf4" : isDeclined ? "#fff7f7" : "#f8fafc";
                const border = isApproved ? "#bbf7d0" : isDeclined ? "#fecaca" : "#e2e8f0";
                const tc     = isApproved ? "#15803d" : isDeclined ? "#dc2626" : "#374151";
                return (
                  <div key={i} className="ta-breakdown-card" style={{ background: bg, borderColor: border }}>
                    <div className="ta-bd-top">
                      <div className="ta-bd-dot" style={{ background: color }} />
                      <span className="ta-bd-key">{d.key}</span>
                    </div>
                    <div className="ta-bd-value" style={{ color: tc }}>{d.original.toLocaleString()}</div>
                    <div className="ta-bd-pct-wrap">
                      <div className="ta-bd-pct-bar" style={{ background: border }}>
                        <div className="ta-bd-pct-fill" style={{ width: `${isNaN(d.value) ? 0 : d.value}%`, background: color }} />
                      </div>
                      <span className="ta-bd-pct-lbl" style={{ color: tc }}>{isNaN(d.value) ? 0 : d.value}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="ta-center">
            <div className="ta-empty-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/>
              </svg>
            </div>
            <p className="ta-empty-title">No analysis data</p>
            <p className="ta-hint">Adjust filters or date range to see results</p>
          </div>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .ta-card {
          font-family: 'Plus Jakarta Sans', sans-serif;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(15,23,42,0.06);
          display: flex; flex-direction: column;
        }

        /* Header */
        .ta-header {
          padding: 20px 24px 16px;
          border-bottom: 1px solid #f1f5f9;
          display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
        }
        .ta-header-left { display: flex; align-items: center; gap: 12px; }
        .ta-icon {
          width: 40px; height: 40px; border-radius: 11px;
          background: linear-gradient(135deg, #eff6ff, #dbeafe);
          border: 1.5px solid #bfdbfe;
          display: flex; align-items: center; justify-content: center;
          color: #2563eb; flex-shrink: 0;
        }
        .ta-title { font-size: 15px; font-weight: 700; color: #0f172a; letter-spacing: -0.2px; margin-bottom: 2px;line-height:22px }
        .ta-subtitle { font-size: 12px; color: #94a3b8; font-weight: 500; }
        .ta-total {
          font-size: 12px; font-weight: 700; padding: 5px 12px;
          border-radius: 100px; background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe;
        }

        /* Body */
        .ta-body { flex: 1; padding: 16px 20px 20px; }
        .ta-layout { display: flex; gap: 20px; align-items: flex-start; flex-wrap: wrap; }
        .ta-chart-col { flex: 0 0 240px; height: 240px; display: flex; align-items: center; justify-content: center; }

        /* Breakdown */
        .ta-breakdown { flex: 1; min-width: 160px; display: flex; flex-direction: column; gap: 10px; justify-content: center; }
        .ta-breakdown-card {
          border: 1.5px solid; border-radius: 12px; padding: 12px 14px;
          transition: transform 0.15s;
        }
        .ta-breakdown-card:hover { transform: translateX(2px); }
        .ta-bd-top { display: flex; align-items: center; gap: 7px; margin-bottom: 6px; }
        .ta-bd-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
        .ta-bd-key { font-size: 12px; font-weight: 600; color: #374151; }
        .ta-bd-value { font-size: 22px; font-weight: 800; letter-spacing: -0.5px; line-height: 1; margin-bottom: 8px; }
        .ta-bd-pct-wrap { display: flex; align-items: center; gap: 8px; }
        .ta-bd-pct-bar { flex: 1; height: 5px; border-radius: 100px; overflow: hidden; }
        .ta-bd-pct-fill { height: 100%; border-radius: 100px; transition: width 0.5s ease; }
        .ta-bd-pct-lbl { font-size: 12px; font-weight: 700; min-width: 32px; text-align: right; }

        /* States */
        .ta-center { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 240px; gap: 10px; }
        .ta-empty-icon { width: 52px; height: 52px; border-radius: 13px; background: #f8fafc; border: 1.5px solid #e2e8f0; display: flex; align-items: center; justify-content: center; color: #cbd5e1; }
        .ta-empty-title { font-size: 13.5px; font-weight: 700; color: #64748b; }
        .ta-hint { font-size: 12px; color: #94a3b8; font-weight: 500; text-align: center; max-width: 220px; }
        .ta-spinner { width: 26px; height: 26px; border: 3px solid #e2e8f0; border-top-color: #2563eb; border-radius: 50%; animation: taSpin 0.7s linear infinite; }
        @keyframes taSpin { to { transform: rotate(360deg); } }

        @media (max-width: 600px) {
          .ta-chart-col { flex: 0 0 100%; height: 200px; }
          .ta-layout { gap: 14px; }
        }
      `}</style>
    </div>
  );
};

export default TransactionAnalysisPieChartCard;